import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase, getCurrentUser, getUserFarms, getAllFarms, getFarmsByOrgViaUserOrgFarm, getOrgFarmCounts } from '../config/supabase';
import { useAuth } from './AuthContext';

// Create Farm Context
const FarmContext = createContext();

// Custom hook to use the Farm Context
export const useFarm = () => {
  const context = useContext(FarmContext);
  if (!context) {
    throw new Error('useFarm must be used within a FarmProvider');
  }
  return context;
};

// Farm Provider Component
export const FarmProvider = ({ children }) => {
  const { isAdmin } = useAuth();
  const [user, setUser] = useState(null);
  const [farms, setFarms] = useState([]);
  const [allFarms, setAllFarms] = useState([]); // Store all farms for filtering
  const [organizations, setOrganizations] = useState([]);
  const [orgFarmCounts, setOrgFarmCounts] = useState({}); // Farm counts per org from both sources
  const [selectedOrgId, setSelectedOrgId] = useState('all');
  const [selectedFarmId, setSelectedFarmId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize farm context on mount AND listen for auth changes
  useEffect(() => {
    initializeFarmContext();
    
    // Listen for auth state changes (login/logout)
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 FarmContext: Auth state changed:', event);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        console.log('👤 User signed in, re-fetching farms...');
        initializeFarmContext();
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out, clearing farms...');
        setUser(null);
        setFarms([]);
        setSelectedFarmId('all');
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  // Initialize: Get user and their farms
  const initializeFarmContext = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔧 Initializing Farm Context...');

      // Get current authenticated user
      const { user: currentUser, error: userError } = await getCurrentUser();
      
      if (userError) {
        console.error('❌ Error getting user:', userError.message);
        setError(userError);
        setLoading(false);
        return;
      }

      if (!currentUser) {
        console.log('ℹ️ No authenticated user found');
        setLoading(false);
        return;
      }

      console.log('✅ User authenticated:', currentUser.email);
      setUser(currentUser);

      // Check admin status directly (don't rely on state which may not be set yet)
      console.log('🔍 Checking admin status for user...');
      const { data: userData, error: adminCheckError } = await supabase
        .from('user')
        .select('is_admin')
        .eq('email', currentUser.email)
        .maybeSingle();
      
      const userIsAdmin = userData?.is_admin || false;
      console.log(`🔐 User admin status: ${userIsAdmin}`);

      // Look up the integer user.id from email, then load every user_org row
      // for that user. From those rows we derive both:
      //   - comp_admin status of any org (existing behaviour)
      //   - the list of orgs the user is a member of (Option B — members can now
      //     opt-in to view an org's farms via the OrganizationSelector)
      let isCompAdmin = false;
      let compAdminOrgId = null;
      let memberOrgRows = [];  // all user_org rows for this user (comp_admin + member)
      if (!userIsAdmin) {
        const { data: userDbData } = await supabase
          .from('user')
          .select('id')
          .eq('email', currentUser.email)
          .maybeSingle();

        if (userDbData) {
          const { data: userOrgs } = await supabase
            .from('user_org')
            .select(`
              organisation,
              role,
              organisation_data:organisation(id, name_org, org_type)
            `)
            .eq('user', userDbData.id);

          memberOrgRows = userOrgs || [];

          const compAdminOrgs = memberOrgRows.filter(r => r.role === 'comp_admin');
          if (compAdminOrgs.length > 0) {
            isCompAdmin = true;
            compAdminOrgId = compAdminOrgs[0].organisation;
            console.log(`🏢 User is comp_admin of org ${compAdminOrgId}`);
          }
          if (memberOrgRows.length > 0) {
            console.log(`🏢 User is connected to ${memberOrgRows.length} org(s)`);
          }
        }
      }

      // Get farms based on user role
      let userFarms;
      let farmsError;
      
      if (userIsAdmin) {
        // Admin users can see ALL farms in the platform
        console.log('👑 Admin user - Loading ALL farms...');
        const result = await getAllFarms();
        userFarms = result.data;
        farmsError = result.error;
        console.log(`👑 Admin user - Loaded ${userFarms?.length || 0} total farms`);
      } else if (isCompAdmin && compAdminOrgId) {
        // comp_admin sees all active farms in their org
        console.log(`🏢 comp_admin - Loading org ${compAdminOrgId} active farms...`);
        const { data: orgFarms, error: orgError } = await supabase.rpc('get_active_org_farms', { p_org_id: compAdminOrgId });
        if (orgError) {
          // Fallback to regular farm loading if RPC doesn't exist yet
          console.warn('⚠️ get_active_org_farms RPC failed, falling back to user farms:', orgError.message);
          const result = await getUserFarms(currentUser.id);
          userFarms = result.data;
          farmsError = result.error;
        } else {
          userFarms = orgFarms || [];
          farmsError = null;
        }
        console.log(`🏢 comp_admin - Loaded ${userFarms?.length || 0} org farms`);
      } else {
        // Regular users see only farms they're assigned to
        console.log('👤 Regular user - Loading assigned farms...');
        const result = await getUserFarms(currentUser.id);
        userFarms = result.data;
        farmsError = result.error;
        console.log(`👤 Regular user - Loaded ${userFarms?.length || 0} accessible farms`);
      }
      
      if (farmsError) {
        console.error('❌ Error getting farms:', farmsError.message);
        setError(farmsError);
        setLoading(false);
        return;
      }

      console.log('✅ Found', userFarms?.length || 0, 'farms');
      setFarms(userFarms || []);
      setAllFarms(userFarms || []); // Store all farms for filtering

      // Load organizations for the OrganizationSelector dropdown.
      //   - Admin: every Private Limited (org_type = 2) organisation on the platform.
      //   - Non-admin: only orgs they're connected to via user_org (comp_admin or member).
      //     Even if they're only "member", they get an org switcher so they can opt into
      //     viewing that org's farms (default view is still their own farms).
      if (userIsAdmin) {
        console.log('👑 Loading organizations with org_type = 2 (Private Limited) for admin...');
        const { data: orgsData, error: orgsError } = await supabase
          .from('organisation')
          .select('id, name_org, org_type')
          .eq('org_type', 2)
          .order('name_org');

        if (!orgsError && orgsData) {
          setOrganizations(orgsData);
          console.log(`👑 Loaded ${orgsData.length} total organizations`);

          const counts = await getOrgFarmCounts();
          setOrgFarmCounts(counts);
          console.log('👑 Loaded org farm counts from both sources');
        }
      } else if (memberOrgRows.length > 0) {
        // Build org list from the joined `organisation_data` (avoids a second
        // round-trip). De-dupe in case a user_org row has duplicates.
        const orgsMap = new Map();
        memberOrgRows.forEach(r => {
          if (r.organisation_data && !orgsMap.has(r.organisation_data.id)) {
            orgsMap.set(r.organisation_data.id, r.organisation_data);
          }
        });
        const orgsList = Array.from(orgsMap.values()).sort((a, b) => (a.name_org || '').localeCompare(b.name_org || ''));
        setOrganizations(orgsList);
        console.log(`🏢 Member user - Loaded ${orgsList.length} accessible org(s)`);

        // Also try to load farm counts so the selector shows them. If the RPC
        // is admin-only the call simply returns nothing and the selector falls
        // back to showing org names without a count.
        try {
          const counts = await getOrgFarmCounts();
          if (counts && Object.keys(counts).length > 0) {
            setOrgFarmCounts(counts);
          }
        } catch (e) {
          // Non-fatal — org names still render.
        }
      }

      // Set default selected farm (first farm or 'all')
      if (userFarms && userFarms.length > 0) {
        setSelectedFarmId('all');
        console.log('✅ Default selection: All Farms');
      }

      setLoading(false);
      console.log('✅ Farm Context initialized successfully');

    } catch (err) {
      console.error('❌ Failed to initialize Farm Context:', err);
      setError(err);
      setLoading(false);
    }
  };

  // Filter farms when organization selection changes
  useEffect(() => {
    const fetchOrgFarms = async () => {
      if (selectedOrgId === 'all') {
        setFarms(allFarms);
      } else {
        const orgId = parseInt(selectedOrgId);
        
        // Get farms from farm.organisation column
        const directFarms = allFarms.filter(f => f.organisation === orgId);
        
        // Get farms from user_org_farm table
        const { data: linkedFarms } = await getFarmsByOrgViaUserOrgFarm(orgId);
        
        // Combine and deduplicate by farm_id
        const farmMap = new Map();
        directFarms.forEach(f => farmMap.set(f.farm_id, f));
        linkedFarms.forEach(f => {
          if (!farmMap.has(f.farm_id)) {
            farmMap.set(f.farm_id, f);
          }
        });
        
        const combinedFarms = Array.from(farmMap.values());
        console.log(`🏢 Org ${orgId}: ${directFarms.length} direct + ${linkedFarms.length} linked = ${combinedFarms.length} total farms`);
        setFarms(combinedFarms);
      }
      // Reset farm selection when org changes
      setSelectedFarmId('all');
    };
    
    fetchOrgFarms();
  }, [selectedOrgId, allFarms]);

  // Get array of active farm IDs based on selection
  const getActiveFarmIds = () => {
    if (selectedFarmId === 'all') {
      return farms.map(f => f.farm_id);
    }
    return [selectedFarmId];
  };

  // Get selected farm object
  const getSelectedFarm = () => {
    if (selectedFarmId === 'all') {
      return {
        farm_id: 'all',
        farm_name: 'All Farms',
        count: farms.length
      };
    }
    return farms.find(f => f.farm_id === selectedFarmId) || null;
  };

  // Refresh farms list
  const refreshFarms = async () => {
    if (!user) return;
    
    console.log('🔄 Refreshing farms...');
    
    // Check admin status
    const { data: userData } = await supabase
      .from('user')
      .select('is_admin')
      .eq('email', user.email)
      .maybeSingle();
    
    const userIsAdmin = userData?.is_admin || false;
    
    let result;
    if (userIsAdmin) {
      console.log('👑 Admin refresh - Loading ALL farms...');
      result = await getAllFarms();
    } else {
      result = await getUserFarms(user.id);
    }
    
    if (!result.error && result.data) {
      setFarms(result.data);
      console.log(`✅ Farms refreshed: ${result.data.length} farms`);
    }
  };

  // Get selected organization object
  const getSelectedOrg = () => {
    if (selectedOrgId === 'all') {
      return { id: 'all', name_org: 'All Organizations' };
    }
    return organizations.find(o => o.id === parseInt(selectedOrgId)) || null;
  };

  // Context value
  const value = {
    // State
    user,
    farms,
    allFarms,
    organizations,
    orgFarmCounts,
    selectedOrgId,
    selectedFarmId,
    loading,
    error,
    isAdmin,
    
    // Actions
    setSelectedOrgId,
    setSelectedFarmId,
    getActiveFarmIds,
    getSelectedFarm,
    getSelectedOrg,
    refreshFarms,
    
    // Utilities
    hasFarms: farms.length > 0,
    farmCount: farms.length,
    hasOrganizations: organizations.length > 0,
    orgCount: organizations.length
  };

  return (
    <FarmContext.Provider value={value}>
      {children}
    </FarmContext.Provider>
  );
};

export default FarmContext;
